import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { AccountApiModel, AccountSummaryApiModel } from '../../api/accounts/models/account.api.model';
import { ConfigService } from '../../Core/Config/services/config.service';
import { userPermissionsEnum } from '../../Core/Users/enums/user-permissions.enum';
import { ServiceApi } from '../genericApi/service-api';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { PreferencesOptionsApiModel } from './models/preferences-options.api.model';
import { PreferencesApiModel, UpdatePreferencesApiModel } from './models/preferences.api.model';
import { SessionTimeoutApiModel } from './models/session-timeout.api.model';
import { UserStateApiModel } from './models/user-state.api.model';
import { UserApiModel } from './models/user.api.model';
import { UserPermissionApiModel } from './models/user.permission.api.model';
import { MfaApiModel } from './models/mfa.api.model';
import { MfaStatusModel, MfaVerifyModel } from '../../Core/Users/models/mfa.model';
import { UserCreateApi } from './models/user-create.api.model';
import { ApiKeyApiModel, CreateApiKeyApiModel } from './models/api-key.api.model';



@Injectable()
export class UsersApi {
    private httpClient: HttpClient;

    constructor(
        private serviceApi: ServiceApi,
        private configurationService: ConfigService,
        private handler: HttpBackend
    ) {
        this.httpClient = new HttpClient(handler);
    }

    /**
    * Gets a user based on the provided ID
    */
    public getUser(id: number): Observable<UserApiModel> {
        const slug = `/users/${id}`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        const response = this.serviceApi.api<UserApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET,
        );

        return response;
    }

    /**
    * Gets the current user's state
    */
    public getCurrentUserState(): Observable<UserStateApiModel> {
        const slug = '/users/me';

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        return this.serviceApi.api<UserStateApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET,
        );
    }

    /**
    * Gets a list of any accounts
    * associated with the provided user ID
    */
    public getAccounts(id: number): Observable<AccountApiModel[]> {
        const slug = `/users/${id}/accounts`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        const response = this.serviceApi.api<AccountApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET,
        );

        return response;
    }

    /**
     * Gets a set of users in a batch
     */
    getAllUsersFromCurrentAccount(): Observable<UserApiModel[]> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        return this.serviceApi.api<UserApiModel[]>(
            '/users',
            null,
            HttpVerbsEnum.GET,
        );
    }

    /**
     * Get general preference lookup values
     */
    getPreferenceLookupValues(): Observable<PreferencesOptionsApiModel> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<PreferencesOptionsApiModel>(
            '/preferences/options',
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Get the conversion rate of currency
     */
    getCurrencyRate(): Observable<any> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            '/currency-rates',
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * Update general preferences (timezone, languages) of the current user
     */
    updateGeneralPreferences(preference: PreferencesApiModel): Observable<null> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<null>(
            '/preferences',
            preference,
            HttpVerbsEnum.PUT
        );
    }
    /**
     * [ADMIN] - get all users from any account
     */
    getAllUsersFromAccount(id: number, permissionId?: number): Observable<UserApiModel[]> {
        const slug: string = `/accounts/${id}/users`;
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<UserApiModel[]>(
            slug,
            null,
            HttpVerbsEnum.GET,
            permissionId != null ? { permissionId } : undefined,
        );
    }

    /**
     * [ADMIN]
     */
    searchAccounts(query: string): Observable<AccountApiModel[]> {
        const queryParams: any = {};

        if (isNaN(Number(query))) {
            if (query.length < 3) {
                return of([]);
            }
            queryParams.name = query;
        } else {
            queryParams.id = query;
        }

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            '/accounts/search/',
            null,
            HttpVerbsEnum.GET,
            queryParams
        ).pipe(map(response => response.results));
    }

    /**
     * [ADMIN]
     */
    searchUsers(query: string, isPrimaryAccountId?: boolean): Observable<UserApiModel[]> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const queryParams: any = {};
        if(isNaN(Number(query))) {
            if (query.length < 3) {
                return of([]);
            }
            queryParams.nameQuery = query;
        } else {
            // if isPrimaryAccountId is true, it searches the users that has its primary account id the same as the given query (account id).
            queryParams[isPrimaryAccountId ? 'primaryAccountId' : 'id'] = query;
        }

        return this.serviceApi.api<any>(
            '/users/search/',
            null,
            HttpVerbsEnum.GET,
            queryParams
        ).pipe(map(response => response.results));
    }

    /**
     * [ADMIN]
     */
    getAccount(id: number): Observable<AccountApiModel> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/accounts/${id}/`;

        return this.serviceApi.api<AccountApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getAccountBatch(ids: number[]): Observable<AccountSummaryApiModel[]> {
        if (ids.length === 0) {
            return of([]);
        }
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        const ACCOUNT_BATCH_SIZE = 200;
        const batches: number[][] = [];
        for (let i = 0; i < ids.length; i += ACCOUNT_BATCH_SIZE) {
            batches.push(ids.slice(i, i + ACCOUNT_BATCH_SIZE));
        }

        const requests = batches.map(batchIds => {
            const slug: string = '/accounts/batch?ids=' + batchIds.join(',');

            return this.serviceApi.api<AccountSummaryApiModel[]>(
                slug,
                null,
                HttpVerbsEnum.GET
            ).pipe(map(x => batchIds.map(id => x[id])));
        });

        return forkJoin(requests).pipe(map(results => [].concat(...results)));
    }

    sendPasswordReset(username: string): Observable<void> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/users/passwordreset?username=${username}` ;

        return this.serviceApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    sendRegistrationEmail(username: string): Observable<void> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/users/registration?username=${username}`;

        return this.serviceApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    getUserNameForToken(token: string): Observable<string> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/users/passwordreset/${token}` ;

        return this.serviceApi.api<string>(
            slug,
            null,
            HttpVerbsEnum.GET,
            {},
            'text'
        );
    }

    setPassword(token: string, password: string): Observable<string> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = '/users/setpassword/' ;

        return this.serviceApi.api<string>(
            slug,
            {
                resetToken: token,
                newPassword: password
            },
            HttpVerbsEnum.POST
        );
    }

    /**
     * [ADMIN]
     */
    createAccount(account: AccountApiModel): Observable<AccountApiModel> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = '/accounts/';

        return this.serviceApi.api<AccountApiModel>(
            slug,
            account,
            HttpVerbsEnum.POST
        );
    }

    /**
     * [ADMIN]
     */
    updateAccount(account: AccountApiModel): Observable<AccountApiModel> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/accounts/${account.id}/`;

        return this.serviceApi.api<AccountApiModel>(
            slug,
            account,
            HttpVerbsEnum.PUT
        );
    }

    /**
     * [ADMIN]
     */
    activateAccount(id: number): Observable<void> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/accounts/${id}/activate`;

        return this.serviceApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    /**
     * [ADMIN]
     */
    deactivateAccount(id: number): Observable<void> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/accounts/${id}/deactivate`;

        return this.serviceApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    /**
     * [ADMIN]
     */
    createUser(user: UserApiModel): Observable<UserCreateApi> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = '/users/';

        return this.serviceApi.api<UserCreateApi>(
            slug,
            user,
            HttpVerbsEnum.POST
        );
    }

    /**
     * [ADMIN]
     */
    updateUser(user: UserApiModel): Observable<void> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/users/${user.id}/`;

        return this.serviceApi.api<void>(
            slug,
            user,
            HttpVerbsEnum.PUT
        );
    }

    /**
     * [ADMIN]
     */
    activateUser(userId: number): Observable<void> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/users/${userId}/activate`;

        return this.serviceApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    /**
     * [ADMIN]
     */
    deactivateUser(userId: number): Observable<void> {
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        const slug: string = `/users/${userId}/deactivate`;

        return this.serviceApi.api<void>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    /**
     * [ADMIN]
     */
    getPermissionsForUser(userId: number): Observable<UserPermissionApiModel[]> {
        const slug = `/users/${userId}/permissions`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * [ADMIN]
     */
    getGlobalPermissionsForUser(userId: number): Observable<userPermissionsEnum[]> {
        const slug = `/users/${userId}/globalpermissions`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
     * [ADMIN]
     */
    updateGlobalPermissionsForUser(userId: number, permissions: userPermissionsEnum[]): Observable<void> {
        const slug = `/users/${userId}/globalpermissions`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            permissions,
            HttpVerbsEnum.PUT
        );
    }

    /**
     * [ADMIN]
     */
    updatePermissions(accountId: number, userId: number, permissions: userPermissionsEnum[]): Observable<UserPermissionApiModel[]> {
        const slug = `/users/${userId}/accounts/${accountId}/permissions`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            permissions,
            HttpVerbsEnum.PUT
        );
    }

    getAllPartnerResellers(): Observable<any> {
        const slug = '/partner-resellers/all';

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    getAllMediaApiPartnerResellers(): Observable<any> {
        const slug = '/mediadbapi/partner-resellers/all';

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    // #region -> Multi-Factor Authentication

    /**
    * [ADMIN]
    */

    getAllMfaSettings(userId: number): Observable<any> {
        const slug = `/users/${userId}/mfa/methods`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    /**
    * [ADMIN]
    */
    resetMfa(userId: number): Observable<void> {
        const slug = `/users/${userId}/mfa/methods`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    /**
    * [ADMIN]
    */
    updateMfaSettingForUser(userId: number, mfa: MfaVerifyModel): Observable<MfaApiModel> {
        const slug = `/users/${userId}/mfa/methods/${mfa.methodId}`;
        const patchModel = {
            isPreferred: mfa.preferred,
            methodId: mfa.methodId,
            name: mfa.name,
        };
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    /**
    * [ADMIN]
    */
    deleteMfaSettingForUser(userId: number, mfaSettingId: number): Observable<void> {
        const slug = `/users/${userId}/mfa/methods/${mfaSettingId}`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    createMfaSetting(): Observable<MfaStatusModel> {
        const slug = '/users/mfa';

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.POST
        );
    }

    updateMfaSetting(mfa: MfaVerifyModel): Observable<MfaApiModel> {
        const slug = `/users/mfa/methods/${mfa.methodId}`;
        const patchModel = {
            isPreferred: mfa.preferred,
            methodId: mfa.methodId,
            name: mfa.name
        };
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            patchModel,
            HttpVerbsEnum.PATCH
        );
    }

    deleteMfaSetting(mfaSettingId: number): Observable<void> {
        const slug = `/users/mfa/methods/${mfaSettingId}`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.DELETE
        );
    }

    getCurrentUserMfaSettings(): Observable<any[]> {
        const slug = '/users/mfa/methods';

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            null,
            HttpVerbsEnum.GET
        );
    }

    sendMfaEmailConfirmation(mfa: MfaVerifyModel): Observable<void> {
        const slug = '/users/mfa/sendverification';
        const verifyModel = {
            email: mfa.email,
            methodId: mfa.methodId,
            username: mfa.username
        };
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            verifyModel,
            HttpVerbsEnum.POST
        );
    }

    verifyMfa(mfa: MfaVerifyModel): Observable<void> {
        const slug = '/users/mfa/verify';
        const verifyModel = {
            name: mfa.name,
            verificationCode: mfa.verificationCode,
            methodId: mfa.methodId
        };
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<any>(
            slug,
            verifyModel,
            HttpVerbsEnum.POST
        );
    }

    //#endregion

    validatePassword(username: string, password: string): Observable<void> {
        let headers = new HttpHeaders();
        headers = headers.set('Authorization', 'Basic ' + btoa(username + ':' + password));
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api(
            '/users/credentials/verify',
            null,
            HttpVerbsEnum.POST,
            null,
            null,
            headers
        );
    }

    getUserApiKey(accountId: number, userId: number): Observable<string> {
        const slug = `/users/${userId}/accounts/${accountId}/apikey`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        return this.serviceApi.api<string>(
            slug,
            null,
            HttpVerbsEnum.GET,
        );
    }

    generateApiKey(accountId: number, userId: number): Observable<string> {
        const slug = `/users/${userId}/accounts/${accountId}/apikey`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        return this.serviceApi.api<string>(
            slug,
            null,
            HttpVerbsEnum.POST,
        );
    }

    /**
     * Gets all API keys for a specific account
     */
    getAccountApiKeys(accountId: number): Observable<ApiKeyApiModel[]> {
        const slug = `/accounts/${accountId}/apikeys`;
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<ApiKeyApiModel[]>(slug, null, HttpVerbsEnum.GET);
    }

    /**
     * Creates a new API key for a specific account
     */
    createAccountApiKey(accountId: number, body: CreateApiKeyApiModel): Observable<ApiKeyApiModel> {
        const slug = `/accounts/${accountId}/apikeys`;
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<ApiKeyApiModel>(slug, body, HttpVerbsEnum.POST);
    }

    /**
     * Updates an API key for a specific account
     */
    updateAccountApiKey(accountId: number, apiKeyId: number, body: Partial<CreateApiKeyApiModel>): Observable<void> {
        const slug = `/accounts/${accountId}/apikeys/${apiKeyId}`;
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<void>(slug, body, HttpVerbsEnum.PUT);
    }

    /**
     * Gets all API keys for the current account
     */
    getApiKeys(): Observable<ApiKeyApiModel[]> {
        const slug = '/accounts/apikeys';
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<ApiKeyApiModel[]>(slug, null, HttpVerbsEnum.GET);
    }

    /**
     * Creates a new API key for the current account
     */
    createApiKey(body: CreateApiKeyApiModel): Observable<ApiKeyApiModel> {
        const slug = '/accounts/apikeys';
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<ApiKeyApiModel>(slug, body, HttpVerbsEnum.POST);
    }

    /**
     * Updates an API key the current account
     */
    updateApiKey(apiKeyId: number, body: Partial<CreateApiKeyApiModel>): Observable<void> {
        const slug = `/accounts/apikeys/${apiKeyId}`;
        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;
        return this.serviceApi.api<void>(slug, body, HttpVerbsEnum.PUT);
    }

    getUserAccountPreferences(accountId: number, userId: number): Observable<UpdatePreferencesApiModel> {
        const slug = `/users/${userId}/accounts/${accountId}/preferences`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        return this.serviceApi.api<UpdatePreferencesApiModel>(
            slug,
            null,
            HttpVerbsEnum.GET,
        );
    }

    updateUserAccountPreferences(accountId: number, userId: number, preference: UpdatePreferencesApiModel): Observable<null> {
        const slug = `/users/${userId}/accounts/${accountId}/preferences`;

        this.serviceApi.apiUrl = this.configurationService.getConfig().apiUrl;

        return this.serviceApi.api<null>(
            slug,
            preference,
            HttpVerbsEnum.PUT,
        );
    }
}



